import { API_BASE } from "config/api";
import { withAuthHeaders } from "services/authHeader";

/* save user's api call */
export async function addUser(formObj) {
  try {
    const url = new URL("api/users/addUser", API_BASE);
    const res = await fetch(url, {
      method: "POST",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
      body: JSON.stringify(formObj),
    });
    const result = await res.json();
    return result;
  } catch (error) {
    console.error("Error while saving user:", error);
    throw error;
  }
}

/**
 * Fetch all users (Admins, Annotators, etc.)
 */
export async function getUsers() {
  try {
    const url = new URL("api/users/getAllUsers", API_BASE);

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const result = await res.json();

    if (result.status === "success") {
      return result.data || [];
    } else {
      throw new Error(result.message || "Failed to fetch users");
    }
  } catch (error) {
    console.error("Error in getUsers:", error);
    throw error;
  }
}

/**
 * Fetch all annotators across all countries and states
 */
export async function getAllAnnotators() {
  try {
    const url = new URL("api/users/getAllAnnotators", API_BASE);

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const result = await res.json();

    if (result.status === "success" && Array.isArray(result.data)) {
      return result.data;
    } else {
      throw new Error(result.message || "Failed to fetch annotators");
    }
  } catch (error) {
    console.error("Error in getAllAnnotators:", error);
    throw error;
  }
}

/**
 * Fetch annotators by a specific state name
 */
export async function getAnnotatorsByState(state) {
  try {
    const url = new URL("api/users/getAnnotatorsByState", API_BASE);
    url.searchParams.set("state", state);

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const result = await res.json();

    if (result.status === "success") {
      return result.data || [];
    } else {
      throw new Error(result.message || "Failed to fetch annotators by state");
    }
  } catch (error) {
    console.error("Error in getAnnotatorsByState:", error);
    throw error;
  }
}

/* 
  Update user details
 */
export async function updateUser(userObj) {
  const url = new URL("api/users/editUserDetailsById", API_BASE);

  const res = await fetch(url, {
    method: "PATCH",
    headers: withAuthHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify(userObj),
  });
  const result = await res.json();
  if (result.status == "success") {
    return result;
  } else {
    throw new Error(result.message || "Failed to update user details");
  }
}

export async function updatePreferredLanguage(language) {
  const url = new URL("api/users/setPreferredLanguage", API_BASE);

  const res = await fetch(url, {
    method: "PATCH",
    headers: withAuthHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify({ language }),
  });

  const result = await res.json();
  if (result.status === "success") {
    return result;
  }
  throw new Error(result.message || "Failed to update preferred language");
}

/* ----------------------------------------------------
   DELETE user account
---------------------------------------------------- */
export async function deleteUser(userId, deleted_by) {
  try {
    const url = new URL("api/users/deleteUser", API_BASE);
    url.searchParams.append("user_id", userId);
    url.searchParams.append("deleted_by", deleted_by);

    const res = await fetch(url, {
      method: "DELETE",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });
    const json = await res.json();
    if (json.status === "success") {
      return {
        status: json.status,
        message: json.message,
      };
    }
    throw new Error(json.message || "Failed to delete user");
  } catch (error) {
    console.error("Error in deleteUser:", error);
    throw error;
  }
}
/* ----------------------------------------------------
   GET all annotators for filter dropdown
---------------------------------------------------- */
export async function getAnnotators() {
  try {
    const url = new URL("api/users/getAllAnnotators", API_BASE);

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const json = await res.json();

    if (json.status === "success") {
      return json.data;
    }

    throw new Error(json.message || "Failed to fetch annotators");
  } catch (error) {
    console.error("Error in getAnnotators:", error);
    throw error;
  }
}

export async function getAnnotatorsIncludingDeleted() {
  try {
    const url = new URL("api/users/getAllAnnotatorsIncludingDeleted", API_BASE);

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const json = await res.json();

    if (json.status === "success") {
      return json.data;
    }

    throw new Error(json.message || "Failed to fetch annotators");
  } catch (error) {
    console.error("Error in getAnnotators:", error);
    throw error;
  }
}
/* ----------------------------------------------------
   GET all annotators of admin for filter dropdown
---------------------------------------------------- */
export const getAllAnnotatorsByAdmin = async (adminId = null) => {
  try {
    let url = new URL("api/users/getAllAnnotatorsByAdmin", API_BASE);

    if (adminId) {
      url += `?adminId=${adminId}`;
    }

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const result = await res.json();

    if (result.status === "success" && Array.isArray(result.data)) {
      return result.data;
    } else {
      throw new Error(result.message || "Failed to fetch annotators");
    }
  } catch (error) {
    console.error("Error fetching annotators:", error);
    throw error;
  }
};

export const getAllAnnotatorsByAdminIncludingDeleted = async (adminId = null) => {
  try {
    let url = new URL("api/users/getAllAnnotatorsByAdminIncludingDeleted", API_BASE);

    if (adminId) {
      url += `?adminId=${adminId}`;
    }

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const result = await res.json();

    if (result.status === "success" && Array.isArray(result.data)) {
      return result.data;
    } else {
      throw new Error(result.message || "Failed to fetch annotators");
    }
  } catch (error) {
    console.error("Error fetching annotators:", error);
    throw error;
  }
};
/* ----------------------------------------------------
   GET all deleted users
---------------------------------------------------- */
export async function getAllDeletedUsers() {
  try {
    const url = new URL("api/users/getAllDeletedUsers", API_BASE);

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const result = await res.json();

    if (result.status === "success") {
      return result.data || [];
    } else {
      throw new Error(result.message || "Failed to fetch deleted users");
    }
  } catch (error) {
    console.error("Error in getAllDeletedUsers:", error);
    throw error;
  }
}
/**
 * Fetch all users including deleted users (Admins, Annotators, etc.)
 */
export async function getAllUsersIncludingDeleted() {
  try {
    const url = new URL("api/users/getAllUsersIncludingDeleted", API_BASE);

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const result = await res.json();

    if (result.status === "success") {
      return result.data || [];
    } else {
      throw new Error(result.message || "Failed to fetch users");
    }
  } catch (error) {
    console.error("Error in getUsers:", error);
    throw error;
  }
}
